/**
 * Re-encodes an image through a canvas to produce a base64 data URL
 * that @react-pdf/renderer can reliably consume.
 *
 * Signed URLs from cloud storage cannot be used directly in PDF generation
 * because @react-pdf/renderer needs a data URL, not an HTTP URL.
 *
 * An optional `onDraw` callback can composite overlays onto the canvas
 * after the base image has been drawn (e.g. zone rectangles for NII reports).
 */
export interface ImageEncodingOptions {
  format?: 'image/png' | 'image/jpeg'
  quality?: number
}

export const PDF_JPEG_ENCODING: ImageEncodingOptions = {
  format: 'image/jpeg',
  quality: 0.84,
}

export async function fetchImageAsBase64(
  url: string,
  signal?: AbortSignal,
  onDraw?: (ctx: CanvasRenderingContext2D, img: HTMLImageElement) => void,
  options: ImageEncodingOptions = {}
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Image conversion was aborted', 'AbortError'))
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'

    const cleanup = () => {
      signal?.removeEventListener('abort', handleAbort)
    }

    const resolveOnce = (value: string) => {
      cleanup()
      resolve(value)
    }

    const rejectOnce = (error: Error) => {
      cleanup()
      reject(error)
    }

    const handleAbort = () => {
      img.onload = null
      img.onerror = null
      img.src = ''
      rejectOnce(new DOMException('Image conversion was aborted', 'AbortError'))
    }

    signal?.addEventListener('abort', handleAbort)

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          rejectOnce(new Error('Failed to get canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0)
        onDraw?.(ctx, img)
        resolveOnce(
          canvas.toDataURL(
            options.format ?? 'image/png',
            options.quality ?? 1.0
          )
        )
      } catch (error) {
        rejectOnce(
          error instanceof Error ? error : new Error('Failed to encode image')
        )
      }
    }

    img.onerror = () =>
      rejectOnce(new Error(`Failed to load image from ${url}`))
    img.src = url
  })
}
